import { FC, useState } from 'react';
import { useMutation } from '@apollo/client';
import escape from 'lodash/escape';

import Page from 'components/common/Page/Page';
import LogSample, { LogObject } from './Components/LogSample/LogSample';
import EditParser from './Components/EditParser/EditParser';
import ParserOutput from './Components/ParserOutput/ParserOutput';
import { TEST_PARSER } from './queries';

import './styles.scss';

type ParserPageProps = {
  parser: string;
};

const ParserPage: FC<ParserPageProps> = ({ parser }: ParserPageProps) => {
  const [parserValue, setParserValue] = useState<string>('');
  const [activeLogLine, setActiveLogLine] = useState<LogObject>(undefined);
  const [parserSample, setParserSample] = useState<Array<string>>([]);
  const [gotDetailedOutput, setGotDetailedOutput] = useState(false);

  const [testParser, { data }] = useMutation(TEST_PARSER);

  const onParserValueChange = (newValue: string): void => {
    setParserValue(newValue);
  };

  // TODO: add error handling
  const requestParserOutput = (newValue?: LogObject, showDetailedOutput = false): void => {
    const testData = (newValue ? newValue?.log : parserSample.join('\n')) || '';
    testParser({
      variables: { testData, showDetailedOutput, textContent: parserValue },
    }).then(() => setGotDetailedOutput(showDetailedOutput));
  };

  const onSelectedLogChange = (newValue: LogObject): void => {
    setActiveLogLine(newValue);
    const showDetailedOutput = Boolean(newValue);
    requestParserOutput(newValue, showDetailedOutput);
  };

  const onParserSampleChanged = (parserSampleArray: Array<string>): void => {
    setParserSample(parserSampleArray);
  };

  if (!parser) {
    return (
      <Page docTitle={`Parser Editor ${parser}`} hasBackground>
        <div>
          Parser names must begin with a letter or digit, and contain only letters, digits, spaces, underscores,
          hyphens, and periods. Please ensure that your parser names follow this format.
        </div>
      </Page>
    );
  }

  return (
    <Page type="full" docTitle={`Parser Editor: ${parser}`} hasBackground>
      <div className="ParserPage">
        <EditParser
          parser={parser}
          parserValue={parserValue}
          onParserValueChange={onParserValueChange}
          className="ParserPage__EditParser"
        />
        <LogSample
          className="ParserPage__LogSample"
          parser={parser}
          activeLogLine={activeLogLine}
          onSelectedLogChange={onSelectedLogChange}
          onParserSampleChanged={onParserSampleChanged}
        />
        <ParserOutput
          className="ParserPage__ParserOutput"
          parserOutputContent={gotDetailedOutput ? data?.testParser?.log : escape(data?.testParser?.log)}
          onUpdateParserOutput={requestParserOutput}
          activeLogLine={activeLogLine}
        />
      </div>
    </Page>
  );
};

export default ParserPage;
